import React, { useEffect, useState, useRef } from "react";
import {
  Table,
  Spin,
  message,
  Empty,
  Input,
  Modal,
  Switch,
  Button,
} from "antd";
import update from "immutability-helper";
import { Link } from "react-router-dom";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { API_BASE_URL } from "../api/api";
import * as XLSX from "xlsx";
import { ExportOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

const type = "DragableBodyColumn";

const DragableHeaderCell = ({
  index,
  moveColumn,
  className,
  style,
  ...restProps
}) => {
  const ref = useRef();

  const [, drop] = useDrop({
    accept: type,
    hover(item) {
      if (!ref.current) return;
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;
      moveColumn(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  const [, drag] = useDrag({
    type,
    item: { index },
  });

  drag(drop(ref));

  return (
    <th
      ref={ref}
      className={className}
      style={{ cursor: "move", userSelect: "none", ...style }}
      {...restProps}
    />
  );
};

function PointsActionLogPage() {
  const [actionLogRecords, setActionLogRecords] = useState([]);
  const [filteredActionLogRecords, setFilteredActionLogRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [columns, setColumns] = useState([]);
  const [search, setSearch] = useState("");

  const fetchPoints = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      message.error("User not authenticated.");
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}pointsActionLog`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const json = await res.json();
      if (json.success && Array.isArray(json.data)) {
        setActionLogRecords(json.data);
      } else {
        message.error("Failed to fetch cash-out points data.");
      }
    } catch (error) {
      console.error(error);
      message.error("Error fetching cash-out points data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPoints();
    setColumns(initColumns);
  }, []);

  useEffect(() => {
    const keyword = search.toLowerCase();
    const result = actionLogRecords.filter(
      (item) =>
        item.member_id.toLowerCase().includes(keyword) ||
        item.operator.toLowerCase().includes(keyword) ||
        item.amount.toString().includes(keyword)
    );
    setFilteredActionLogRecords(result);
  }, [search, actionLogRecords]);

  const initColumns = [
    {
      title: "ID",
      dataIndex: "id",
      sorter: (a, b) => a.id - b.id,
    },
    {
      title: "Member ID",
      dataIndex: "member_id",
      render: (member_id) => <Link to={`/user/${member_id}`}>{member_id}</Link>,
      sorter: (a, b) => a.member_id.localeCompare(b.member_id),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      sorter: (a, b) => parseFloat(a.amount) - parseFloat(b.amount),
    },
    {
      title: "Reason",
      dataIndex: "reason",
      sorter: (a, b) => a.reason.localeCompare(b.reason),
    },
    {
      title: "Operator",
      dataIndex: "operator",
      sorter: (a, b) => a.operator.localeCompare(b.operator),
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      sorter: (a, b) => (a.created_at || "").localeCompare(b.created_at || ""),
      render: (value) =>
        value ? dayjs.utc(value).local().format("YYYY-MM-DD HH:mm") : "-",
    },
  ];

  const moveColumn = (dragIndex, hoverIndex) => {
    const dragColumn = columns[dragIndex];
    setColumns(
      update(columns, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, dragColumn],
        ],
      })
    );
  };

  const components = {
    header: {
      cell: (props) => (
        <DragableHeaderCell {...props} moveColumn={moveColumn} />
      ),
    },
  };

  const finalColumns = columns.map((col, index) => ({
    ...col,
    onHeaderCell: () => ({
      index,
      moveColumn,
    }),
  }));

  const handleExport = () => {
    const exportRows = filteredActionLogRecords.map((record) => {
      const exportRow = {};
      columns.forEach((col) => {
        const value = record[col.dataIndex];

        if (col.renderExport) {
          exportRow[col.title] = col.renderExport(value, record);
        } else if (col.render) {
          const rendered = col.render(value, record);
          exportRow[col.title] =
            typeof rendered === "string" ? rendered : value;
        } else {
          exportRow[col.title] = value;
        }
      });
      return exportRow;
    });

    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Points Action Log");
    XLSX.writeFile(workbook, "points_action_log.xlsx");
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", paddingTop: 50 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!actionLogRecords.length) {
    return (
      <div style={{ textAlign: "center", paddingTop: 50 }}>
        <Empty description="No cash-out data found" />
      </div>
    );
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <div style={{ padding: 24 }}>
        <h2>Points Cash-Out</h2>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <Input
            placeholder="Search by Member ID, Email, or Cash Amount"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ marginBottom: 16, width: 370 }}
            allowClear
          />
          <Button
            type="primary"
            onClick={handleExport}
            style={{
              fontSize: "16px",
              fontWeight: "bold",
              borderRadius: "8px",
              backgroundColor: "rgba(0, 128, 0, 0.1)", // Soft green background
              color: "#008000", // Strong green text color
              border: "1px solid #008000", // Green border
              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
              transition: "background-color 0.3s ease, transform 0.3s ease",
            }}
            icon={<ExportOutlined />}
            onMouseOver={(e) =>
              (e.target.style.backgroundColor = "rgba(0, 128, 0, 0.2)")
            } // Slightly darker green on hover
            onMouseOut={(e) =>
              (e.target.style.backgroundColor = "rgba(0, 128, 0, 0.1)")
            } // Revert to soft green
          >
            Export to Excel
          </Button>
        </div>

        <div
          style={{
            margin: "16px 0",
            padding: "8px 16px",
            backgroundColor: "#fafafa",
            border: "1px solid #d9d9d9",
            borderRadius: "8px",
            fontWeight: 500,
            fontSize: "14px",
            color: "#555",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
          }}
        >
          Showing{" "}
          <span style={{ color: "#1890ff", fontWeight: 600 }}>
            {filteredActionLogRecords.length}
          </span>{" "}
          result{filteredActionLogRecords.length !== 1 ? "s" : ""} found
        </div>
        <div className="compact-table-wrapper">
          <Table
            dataSource={filteredActionLogRecords}
            columns={finalColumns}
            rowKey="id"
            components={components}
            bordered
            scroll={{ x: "max-content" }}
            pagination={{
              defaultPageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: ["10", "25", "50", "100"],
              onShowSizeChange: (current, size) => {}, // optional
            }}
          />
        </div>
      </div>
    </DndProvider>
  );
}

export default PointsActionLogPage;
